import React from 'react';
import {Typography} from '@mui/material';
import InlineRenderer from './InlineRenderer';
import Blockquote from './Blockquote';
import {BulletList, ListItem, OrderedList} from './ListRenderer';
import {Table, TableCell, TableRow} from './TableRenderer';
import ImageRenderer from './ImageRenderer';
import {isEqual} from 'lodash';

const NodeRenderer = ({
                          node, idx, inTable = false, inListItem = false, prefixText,
                          parentPath = [], nestedIndexesTree, highlightedRef
                      }) => {
    if (!node) return null;
    const key = `node-${idx}`;

    const isIdxArray = Array.isArray(idx);
    const currentPath = isIdxArray ? [...parentPath, ...idx] : [...parentPath, idx];
    const isHighlighted = isEqual(currentPath, nestedIndexesTree);

    const highlightStyle = {
        backgroundColor: isHighlighted ? 'rgba(255, 255, 0, 0.3)' : 'transparent',
        borderRadius: '4px',
        display: 'block', // Ensure the highlight covers the block
    };

    const formatClassification = (attrs = {}) => {
        const level = attrs.classificationLevel || attrs.classification_access || 'U';
        const distribution = Array.isArray(attrs.distribution) ? attrs.distribution : [];
        const compartment = Array.isArray(attrs.compartment) ? attrs.compartment : [];

        let classification = level || 'U';
        if (distribution.length > 0) classification += `//${distribution.join('/')}`;
        if (compartment.length > 0) classification += `//${compartment.join('/')}`;
        return classification;
    };

    const getClassification = (n) => formatClassification(n?.attrs || {});

    // renderNode callback passed to extracted components so they can recurse without importing NodeRenderer
    const renderNode = (n, i, opts = {}) => (
        <NodeRenderer
            key={i}
            node={n}
            idx={i}
            inTable={opts.inTable || false}
            inListItem={opts.inListItem || false}
            prefixText={opts.prefixText}
            parentPath={currentPath}
            nestedIndexesTree={nestedIndexesTree}
            highlightedRef={highlightedRef}
        />
    );

    switch (node.type) {
        case 'title':
            return null
        case 'blockquote':
            return (
                <Blockquote
                    ref={isHighlighted ? highlightedRef : null} sx={highlightStyle}
                    node={node}
                    idx={idx}
                    inTable={inTable}
                    inListItem={inListItem}
                    renderNode={renderNode}
                    isHighlighted
                    highlightStyle
                />
            );
        case 'heading': {
            const level = (node.attrs && node.attrs.level) || 1;
            const variant = level === 1 ? 'h4' : level === 2 ? 'h5' : 'h6';
            const showOwnPrefix = !inListItem && (node.content?.length > 0);
            return (
                <Typography
                    ref={isHighlighted ? highlightedRef : null}
                    key={key} variant={variant} gutterBottom={!inTable}
                    sx={{mb: inTable ? 0 : undefined, ...(isHighlighted ? highlightStyle : {})}}>
                    {showOwnPrefix && `(${getClassification(node)}) `}
                    {prefixText && `${prefixText} `}
                    {(node.content || []).map((c, i) => (
                        <InlineRenderer key={`${key}-c-${i}`} node={c} nodeKey={i}/>
                    ))}
                </Typography>
            );
        }
        case 'paragraph': {
            // Keep rendering image-like children via NodeRenderer for backward compatibility payloads.
            const hasBlockNodes = (node.content || []).some(c =>
                c.type === 'image' || c.type === 'link_embed' || c.type === 'agenticSection'
            );

            if (hasBlockNodes) {
                // Render block-level nodes directly
                return (
                    <React.Fragment key={key}>
                        {(node.content || []).map((c, i) => {
                            // For block-level nodes, use NodeRenderer
                            if (c.type === 'image' || c.type === 'link_embed' || c.type === 'agenticSection') {
                                return renderNode(c, `${key}-p-${i}`, {inTable, inListItem});
                            }
                            // For inline content, use InlineRenderer
                            return (
                                <InlineRenderer key={`${key}-p-${i}`} node={c} nodeKey={i}/>
                            );
                        })}
                    </React.Fragment>
                );
            }

            // Regular paragraph with only inline content
            return (
                <Typography
                    key={key}
                    component="p"
                    ref={isHighlighted ? highlightedRef : null}
                    sx={{mb: inTable ? 0 : 2, ...(isHighlighted ? highlightStyle : {})}}
                >
                    {/* show prefix for non-list paragraphs; also allow injected prefixText from list items */}
                    {!inListItem && (node.content?.length > 0) && `(${getClassification(node)}) `}
                    {prefixText && `${prefixText} `}
                    {(node.content || []).map((c, i) => (
                        <InlineRenderer key={`${key}-p-${i}`} node={c} nodeKey={i}/>
                    ))}
                </Typography>
            );
        }
        case 'ordered_list':
        case 'orderedList':
            return <OrderedList
                highlightedRef={highlightedRef}
                isHighlighted={isHighlighted}
                node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'bullet_list':
        case 'bulletList':
            return <BulletList node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'list_item':
        case 'listItem':
            return <ListItem
                highlightedRef={highlightedRef}
                isHighlighted={isHighlighted}
                node={node} idx={idx} inTable={inTable} renderNode={renderNode}/>;
        case 'table':
            return <Table node={node} idx={idx} renderNode={renderNode}/>;
        case 'table_row':
        case 'tableRow':
            return <TableRow node={node} idx={idx} renderNode={renderNode}/>;
        case 'table_cell':
        case 'table_header': {
            const normalizedNode = node.type === 'table_header' ? {...node, type: 'tableHeader'} : node;
            return <TableCell
                node={normalizedNode} idx={idx}
                highlightedRef={highlightedRef}
                isHighlighted={isHighlighted}
                renderNode={renderNode}/>;
        }
        case 'tableCell':
        case 'tableHeader':
            return <TableCell
                node={node} idx={idx}
                highlightedRef={highlightedRef}
                isHighlighted={isHighlighted}
                renderNode={renderNode}/>;
        case 'image':
            return (
                <ImageRenderer
                    node={node}
                    idx={idx}
                    inTable={inTable}
                    inListItem={inListItem}
                    prefixText={prefixText}
                />
            );
        case 'link_embed': {
            const href = node.attrs?.href || node.attrs?.url;
            if (!href) return null;
            const label = node.attrs?.title || node.attrs?.label || href;
            return (
                <Typography
                    ref={isHighlighted ? highlightedRef : null}
                    key={key}
                    component="a"
                    href={href}
                    target={node.attrs?.target || '_blank'}
                    rel={node.attrs?.rel || 'noopener noreferrer'}
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        display: 'inline-block',
                        ...(isHighlighted ? highlightStyle : {})
                    }}
                >
                    {prefixText && `${prefixText} `}
                    {label}
                </Typography>
            );
        }
        case 'agenticSection':
            return (
                <React.Fragment key={key}>
                    {(node.content || []).map((c, i) => renderNode(c, i, {inTable, inListItem}))}
                </React.Fragment>
            );
        case 'text':
            return <InlineRenderer node={node} nodeKey={key}/>;
        default:
            if (node.content && Array.isArray(node.content)) {
                return (
                    <React.Fragment key={key}>
                        {node.content.map((c, i) => (
                            renderNode(c, i, {inTable, inListItem})
                        ))}
                    </React.Fragment>
                );
            }
            return null;
    }
};

export default NodeRenderer;
